// src/components/inquiries/InquiryDetailPanel.js
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchInquiry, updateInquiry, sendInquiryEmail } from '../store/thunks/inquiriesActions';
import { format } from 'date-fns';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ConnectGmailButton from '../components/leadsComponents/ConnectGmailButton';
import EmailThread from '../components/leadsComponents/EmailThread';

export default function InquiryDetailPanel({ inquiryId, onClose }) {
  const dispatch = useDispatch();
  const { current: inquiry, loading } = useSelector(state => state.inquiries);
  const { templates } = useSelector(state => state.templates);
  const adminInfo = useSelector(state => state.login.adminInfo);

  const gmailConnected = Boolean(adminInfo?.automationAllowed);
  const responderName = `${adminInfo?.firstName || ''} ${adminInfo?.lastName || ''}`.trim();

  const [notes, setNotes] = useState('');
  const [type, setType] = useState('Other');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [replyBody, setReplyBody] = useState('');

  useEffect(() => { if (inquiryId) dispatch(fetchInquiry(inquiryId)); }, [dispatch, inquiryId]);
  useEffect(() => { if (inquiry) { setNotes(inquiry.notes || ''); setType(inquiry.type || 'Other'); } }, [inquiry]);

  const handleSaveNotes = () => dispatch(updateInquiry(inquiryId, { notes }));
  const handleTypeChange = e => { const newType = e.target.value; setType(newType); dispatch(updateInquiry(inquiryId, { type: newType })); };
  const handleTemplateSelect = e => { const tmplId = e.target.value; setSelectedTemplate(tmplId); const tmpl = templates.find(t => t.id === tmplId); setReplyBody(tmpl?.body || ''); };
  const handleSendReply = () => {
    if (!gmailConnected || !replyBody.trim()) return;
    const subject = inquiry.subject ? `Re: ${inquiry.subject}` : `Re: Your Inquiry from ${inquiry.name}`;
    dispatch(sendInquiryEmail({
      id: inquiryId,
      from: adminInfo.gmail,
      to: inquiry.email,
      subject,
      body: replyBody,
      threadId: inquiry.threadId,
      responder: responderName,
      templateUsed: selectedTemplate,
      userId: adminInfo._id
    }))
    .then(() => { setSelectedTemplate(''); setReplyBody(''); dispatch(fetchInquiry(inquiryId)); onClose(); })
    .catch(console.error);
  };

  if (loading || !inquiry) {
    return <div className="flex-1 flex items-center justify-center"><div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="flex flex-col h-full p-6 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
  <h2 className="text-xl font-semibold text-[#1F2937]">Inquiry Details</h2>
  <div className="flex space-x-2">
    <button
      onClick={() => {
        dispatch(updateInquiry(inquiryId, { closed: true }));
        onClose();
      }}
      className="px-3 py-1 rounded-[12px] bg-[#EF4444] text-white text-sm hover:bg-[#DC2626]"
    >
      Close Inquiry
    </button>
    <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F3F4F6]">
      <XMarkIcon className="h-5 w-5 text-[#6B7280]" />
    </button>
  </div>
</div>
      {/* Inquiry Info */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        <div><p className="text-sm text-[#6B7280]">Name</p><p className="text-base font-medium text-[#1F2937]">{inquiry.name}</p></div>
        <div><p className="text-sm text-[#6B7280]">Email</p><p className="text-base font-medium text-[#1F2937]">{inquiry.email}</p></div>
        <div><p className="text-sm text-[#6B7280]">Phone</p><p className="text-base font-medium text-[#1F2937]">{inquiry.phone}</p></div>
        <div><p className="text-sm text-[#6B7280]">Date</p><p className="text-base font-medium text-[#1F2937]">{format(new Date(inquiry.createdAt), 'PPpp')}</p></div>
        <div className="col-span-2"><p className="text-sm text-[#6B7280]">Message</p><div className="mt-2 p-4 bg-[#F9FAFB] rounded-lg whitespace-pre-wrap text-[#374151]">{inquiry.message}</div></div>
        <div><p className="text-sm text-[#6B7280]">Type</p><select className="mt-1 w-full p-2 border border-[#D1D5DB] rounded-lg" value={type} onChange={handleTypeChange}>{['Guest','Driver','Host','Other'].map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></div>
      </div>

      {/* Internal Notes */}
      <div className="mb-6"><p className="text-sm text-[#6B7280]">Internal Notes</p><textarea className="mt-2 w-full h-24 p-3 border border-[#D1D5DB] rounded-lg focus:outline-none" value={notes} onChange={e => setNotes(e.target.value)} /><button onClick={handleSaveNotes} className="mt-2 px-4 py-2 rounded-[12px] bg-[#2563EB] text-white hover:bg-[#1E40AF]">Save Notes</button></div>

      {/* Gmail Connection Prompt */}
      {!gmailConnected && <div className="mb-6"><p className="text-sm text-red-600 mb-2">To send replies, please connect your Gmail account.</p><ConnectGmailButton /></div>}

      {/* Responder Display */}
      <div className="mb-4"><p className="text-sm text-[#6B7280]">Responder</p><p className="text-base font-medium text-[#1F2937]">{responderName || '—'}</p></div>

      {/* Email Thread */}
      <div className="mb-6">
        <p className="text-sm text-[#6B7280] mb-2">Email Thread</p>
        <EmailThread
          messages={inquiry.messages}
          inquiryId={inquiryId}         // pass the correct prop name
          threadId={inquiry.threadId}   // (optional) so it can fetch live from Gmail
          key={inquiry.threadId || inquiryId}  // force re-render when thread changes
        />
      </div>

      {/* Reply Section */}
      <div className="mb-6"><p className="text-sm text-[#6B7280]">Send Reply</p><div className="flex space-x-2 mt-2 mb-2"><select className="flex-1 p-2 border border-[#D1D5DB] rounded-lg" value={selectedTemplate} onChange={handleTemplateSelect} disabled={!gmailConnected}><option value="">— Select template —</option>{templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></div><textarea className="mt-1 w-full h-28 p-3 border border-[#D1D5DB] rounded-lg focus:outline-none" value={replyBody} onChange={e => setReplyBody(e.target.value)} disabled={!gmailConnected} />
        {replyBody && <div className="bg-white border border-[#E5E7EB] rounded-[12px] p-4 shadow-sm mt-4 mb-4"><h4 className="text-sm font-semibold text-[#1F2937] mb-2">Live Preview</h4><div className="text-sm text-[#374151] whitespace-pre-line" dangerouslySetInnerHTML={{ __html: replyBody }} /></div>}
        <button onClick={handleSendReply} disabled={!gmailConnected || !replyBody.trim()} className={`mt-2 px-4 py-2 rounded-[12px] font-medium text-white transition-colors duration-150 ${!gmailConnected ? 'bg-[#9CA3AF] cursor-not-allowed' : (!replyBody.trim() ? 'bg-[#9CA3AF] cursor-not-allowed' : 'bg-[#10B981] hover:bg-[#047857]')}`}>{gmailConnected ? 'Send Reply' : 'Connect Gmail to Reply'}</button></div>

      {/* Response History */}
      <div className="flex-1 overflow-y-auto"><p className="text-sm text-[#6B7280] mb-2">Response History</p>{inquiry.responses.slice().reverse().map((resp, idx) => <div key={idx} className="mb-4 p-4 bg-[#F9FAFB] rounded-2xl"><div className="flex justify-between items-center mb-2"><span className="text-sm font-medium text-[#1F2937]">{resp.responder}</span><span className="text-xs text-[#6B7280]">{format(new Date(resp.respondedAt), 'PPpp')}</span></div><div className="text-[#374151] whitespace-pre-wrap">{resp.message}</div>{resp.templateUsed && <div className="mt-2 text-xs text-[#9CA3AF]">Template: {resp.templateUsed}</div>}</div>)}</div>
    </div>
  );
}
